import { createSelector } from 'reselect'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'

import { ReduxState } from '@/utils/typeHelpers'
import {
  AnnualTaxDetail,
  AnnualTaxDetailStatuses,
} from './annualTaxDetails.slice'
import { convertUtcToLocalDate } from '@/utils/dateHelpers'
import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { logHeardError } from '@/utils/errorHelpers'

export const selectAnnualTaxDetails = (state: ReduxState) =>
  state.annualTaxDetails

/*
  Select the single "current" record if exists
*/
export const selectCurrentAnnualTaxDetails = createSelector(
  [selectAnnualTaxDetails],
  (annualTaxDetails) =>
    Object.values(annualTaxDetails).find(
      (d) => d.status === AnnualTaxDetailStatuses.current
    )
)

/*
  Select the single "current" year if exists, if not default
*/
export const selectCurrentAnnualTaxYear = createSelector(
  [selectCurrentAnnualTaxDetails],
  (annualTaxDetails): string => {
    const now = DateTime.utc()
    const taxYearDefault =
      now.month < 11
        ? now.minus({ years: 1 }).year.toString()
        : now.year.toString()
    return annualTaxDetails?.taxYear || taxYearDefault
  }
)

/*
  Selects all records sorted by taxYear desc
*/
export const selectAllAnnualTaxDetailsSorted = createSelector(
  [selectAnnualTaxDetails],
  (annualTaxDetails) => {
    return orderBy(
      Object.values(annualTaxDetails),
      [(d) => Number(d.taxYear)],
      ['desc']
    )
  }
)

/*
   Select by year
*/
export const selectTaxDetailsByYear = createSelector(
  selectAnnualTaxDetails,
  (_: unknown, year: string) => year,
  (annualTaxDetails, year) => {
    return Object.values(annualTaxDetails).find((d) => d.taxYear === year)
  }
)

// Duplicated from user.selectors.ts due to circular dependency
const selectTaxEntityType = (state: ReduxState) =>
  state.auth.user?.financialProfile?.taxEntityType

/**
 * Determines if a user with the given tax entity type can opt into
 * previous year taxes based on the current annual tax details cutoff dates.
 * This should ONLY be used for SIGNUP FLOW. Non-signup flow use cases should use selectIsEligibleForCurrentYearTaxServices instead.
 */
export const canOptIntoPreviousYearTaxes = (
  taxDetails: AnnualTaxDetail | undefined,
  taxEntity: string | null | undefined
): boolean => {
  if (!taxDetails) {
    return false
  }
  const optInCutOff =
    taxEntity === TAX_ENTITY_TYPES.form_1120_s
      ? convertUtcToLocalDate(
          taxDetails?.form_1120_s_new_user_tax_cutoff_date
        )?.endOf('day')
      : convertUtcToLocalDate(
          taxDetails?.form_1040_new_user_tax_cutoff_date
        )?.endOf('day')

  const lateJoinerAfterDate = convertUtcToLocalDate(
    taxDetails?.new_user_cutoff_date
  )?.endOf('day')
  if (!optInCutOff?.isValid || !lateJoinerAfterDate?.isValid) {
    logHeardError('Missing or invalid opt in cutoff date', {
      optInCutOff,
      lateJoinerAfterDate,
    })
    return false
  }
  const now = DateTime.now()
  return now > lateJoinerAfterDate && now <= optInCutOff
}

/**
 * Redux selector for opt-in eligibility using state.
 * Prefer canOptIntoPreviousYearTaxes() directly when you have
 * fresh values not yet reflected in Redux (e.g. after form submission).
 * This should ONLY be used for SIGNUP FLOW. Non-signup flow use cases should use selectIsEligibleForCurrentYearTaxServices instead.
 */
export const selectCanOptIntoPreviousTaxYear = createSelector(
  selectCurrentAnnualTaxDetails,
  selectTaxEntityType,
  canOptIntoPreviousYearTaxes
)

const getBaseNonRelevantReasons = (taxYear: string) => [
  { text: `I haven't used this vendor in ${taxYear}`, value: 'notUsed' },
  {
    text: "I don't have access to this vendor anymore",
    value: 'noAccess',
  },
]

const BELOW_THRESHOLD_REASON = {
  text: 'I did not meet the threshold to receive a 1099-K',
  value: 'belowThreshold',
}

export const selectNonRelevantDocumentReasons = createSelector(
  selectCurrentAnnualTaxYear,
  (taxYear) => [...getBaseNonRelevantReasons(taxYear), BELOW_THRESHOLD_REASON]
)

export const selectNonRelevantDocumentReasonsByCategoryCode = createSelector(
  selectCurrentAnnualTaxYear,
  (_: unknown, categoryCode?: string) => categoryCode,
  (taxYear, categoryCode) => {
    const reasons = getBaseNonRelevantReasons(taxYear)

    if (categoryCode === UserDocumentCategoryIdentifier.form1099k) {
      reasons.push(BELOW_THRESHOLD_REASON)
    }

    return reasons
  }
)

const PROVIDED_ELSEWHERE_REASON = {
  text: "I've already provided this information elsewhere",
  value: 'providedElsewhere',
}

export const selectNonRelevantDocumentReasonsByCategoryCodeAndMerchant =
  createSelector(
    [
      selectNonRelevantDocumentReasonsByCategoryCode,
      (_: unknown, _categoryCode?: string, merchantName?: string) =>
        merchantName,
    ],
    (baseReasons, merchantName) => {
      return merchantName?.includes('Stripe')
        ? [...baseReasons, PROVIDED_ELSEWHERE_REASON]
        : baseReasons
    }
  )

export const selectIsAfter1099NECDueDate = createSelector(
  selectCurrentAnnualTaxDetails,
  (annualTaxDetails) => {
    if (annualTaxDetails?.form_1099_nec_due_date) {
      const dueDate = convertUtcToLocalDate(
        annualTaxDetails.form_1099_nec_due_date
      )?.endOf('day')
      return dueDate?.isValid ? DateTime.now() > dueDate : false
    }
    return false
  }
)
